// Stable per-install identifier.
//
// The fleet's machines are identically patched, so `navigator.userAgent` is
// byte-identical across them — useless as a machine key. This random id,
// persisted in localStorage, is the closest thing the frontend has to
// "which machine is this", and it is what turns raw GPU-reset events into a
// per-machine-per-day rate (docs/gpu-load-investigation.md, Phase 0).
//
// Two consumers, deliberately sharing one value:
//   - `getDeviceAppId()` in services/apiRequest.js — session identity
//     (see the shared-UA token clobber note there).
//   - the GPU-reset watchdog — telemetry attribution.
//
// Caveats for anyone reading the metric: clearing site data or reinstalling the
// container mints a new id, so an install can appear twice over a long window,
// and one machine re-imaged mid-measurement looks like two machines.
const INSTALL_ID_KEY = "mrgInstallId";

const randomId = (length) => {
    const possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const bytes = crypto.getRandomValues(new Uint8Array(length));
    let text = "";
    for (let i = 0; i < length; i++) {
        text += possible.charAt(bytes[i] % possible.length);
    }
    return text;
};

// Returns null — never a fabricated value — when localStorage is unavailable.
// Callers decide how to degrade; a per-call random id would be worse than
// nothing for both session identity and telemetry counting.
export const getInstallId = () => {
    try {
        let installId = localStorage.getItem(INSTALL_ID_KEY);
        if (!installId) {
            installId = randomId(20);
            localStorage.setItem(INSTALL_ID_KEY, installId);
        }
        return installId;
    } catch {
        return null;
    }
};
